import { useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useTenant } from '../contexts/TenantContext';
import GooglePlacesSearch from '../components/GooglePlacesSearch';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Onboarding component for new users to create their business.
 */
export default function Onboarding() {
  const { getToken } = useAuth();
  const { refreshTenant } = useTenant();
  const [businessName, setBusinessName] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [googlePlaceId, setGooglePlaceId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handlePlaceSelect = (place) => {
    if (place) {
      setGooglePlaceId(place.place_id);
      // Auto-fill business name if empty
      if (!businessName && place.name) {
        setBusinessName(place.name);
      }
    } else {
      setGooglePlaceId(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const token = await getToken();
      const response = await fetch(`${BACKEND_URL}/api/onboarding/business`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          business_name: businessName,
          first_name: firstName || null,
          last_name: lastName || null,
          google_place_id: googlePlaceId || null,
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to create business');
      }

      const data = await response.json();
      console.log('Business created successfully:', data);

      // Refresh tenant context to load the new business
      await refreshTenant();

      // If we're still here and no error, it means refresh succeeded
      console.log('Tenant refreshed successfully');
    } catch (err) {
      console.error('Onboarding error:', err);
      setError(err.message);
      setLoading(false); // Reset loading on error
    }
    // Don't reset loading on success - let the page transition happen
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-amber-50 flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-3 mb-6">
            <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" fill="#f97316" viewBox="0 0 256 256">
              <path d="M169.57,72.59A80,80,0,0,0,16,104v64a16,16,0,0,0,16,16H86.67A80.15,80.15,0,0,0,160,232h64a16,16,0,0,0,16-16V152A80,80,0,0,0,169.57,72.59ZM32,104a64,64,0,1,1,64,64H32ZM224,216H160a64.14,64.14,0,0,1-55.68-32.43A79.93,79.93,0,0,0,174.7,89.71,64,64,0,0,1,224,152Z"></path>
            </svg>
            <span className="text-2xl font-bold text-gray-900">DAZZA</span>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Welcome to DAZZA!</h1>
          <p className="text-gray-600">Let&apos;s set up your business to get started</p>
        </div>

        <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-2">
                Business Name *
              </label>
              <input
                type="text"
                required
                value={businessName}
                onChange={(e) => setBusinessName(e.target.value)}
                placeholder="e.g., Joe's Bistro"
                className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-orange-400 focus:outline-none focus:ring-2 focus:ring-orange-400/20"
                data-testid="onboarding-business-name"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">
                  First Name
                </label>
                <input
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="John"
                  className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-orange-400 focus:outline-none focus:ring-2 focus:ring-orange-400/20"
                  data-testid="onboarding-first-name"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">
                  Last Name
                </label>
                <input
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder="Smith"
                  className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-orange-400 focus:outline-none focus:ring-2 focus:ring-orange-400/20"
                  data-testid="onboarding-last-name"
                />
              </div>
            </div>

            {/* Google Places search */}
            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-2">
                Find your business on Google
              </label>
              <GooglePlacesSearch
                onSelect={handlePlaceSelect}
                getToken={getToken}
                placeholder="Search your business name..."
              />
              <button
                type="button"
                onClick={() => setGooglePlaceId(null)}
                className="mt-1.5 text-xs text-gray-400 hover:text-gray-600"
              >
                Skip this step
              </button>
            </div>

            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading || !businessName}
              className="w-full py-3 px-4 bg-gradient-to-r from-orange-500 to-orange-600 text-white font-medium rounded-lg hover:from-orange-600 hover:to-orange-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              data-testid="onboarding-submit"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Creating...
                </span>
              ) : (
                'Create Business'
              )}
            </button>
          </form>

          <div className="mt-6 pt-6 border-t border-gray-100">
            <div className="flex items-start gap-3 text-sm text-gray-500">
              <svg className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span>Your account includes free SMS credits to get started. No credit card required.</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
